// English UI strings — sparse, primarily for future tenant portal
// Admin portal is Danish-only per product spec

export const en = {
  auth: {
    login: 'Sign in',
    logout: 'Sign out',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: '••••••••',
    loginButton: 'Sign in',
    loggingIn: 'Signing in...',
    loginError: 'Invalid email or password',
    required: 'This field is required',
    invalidEmail: 'Invalid email address',
    passwordMin: 'Password must be at least 6 characters',
    passwordMin8: 'Password must be at least 8 characters',
    nameLabel: 'Full name',
    namePlaceholder: 'Jane Doe',
    nameMin: 'Name must be at least 2 characters',
    companyLabel: 'Company',
    companyPlaceholder: 'My Company ApS',
    companyMin: 'Company name is required',
    signup: 'Create account',
    signingUp: 'Creating...',
    signupSubtitle: 'Create a new account',
    signupError: 'Something went wrong. Please try again.',
    alreadyAccount: 'Already have an account?',
    loginLink: 'Sign in here',
    noAccountYet: 'No account yet?',
    createAccountLink: 'Create one here',
  },
  nav: {
    dashboard: 'Dashboard',
    properties: 'Properties',
    companies: 'Companies',
    leases: 'Leases',
    contracts: 'Lejeforhold',
    invoices: 'Invoices',
    renters: 'Renters',
    settings: 'Settings',
    virksomhed: 'Virksomhed',
    financial: 'Financial',
    arrears: 'Arrears',
    communications: 'Communications',
    exports: 'Lists',
    listings: 'Listings',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Hello,',
    subtitle: "Here's your overview for today.",
    loading: 'Loading...',
    companies: {
      title: 'Key figures per company',
      filterLabel: 'Company',
      allCompanies: 'All companies',
      total: 'Total',
      colCompany: 'Company',
      colLeases: 'Units',
      colOccupancy: 'Occupancy rate',
      colCollected: 'Collected (this month)',
      colArrears: 'Arrears',
      noData: 'No data',
    },
  },
  general: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    loading: 'Loading...',
    error: 'Something went wrong. Please try again.',
    notFound: 'Page not found.',
    backToHome: 'Back to home',
    yes: 'Yes',
    no: 'No',
  },
  brand: {
    name: 'Boligflow',
    tagline: 'Property management without the hassle',
  },
  renters: {
    title: 'Renters',
    create: 'Create renter',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    cpr: 'CPR number',
    cprPlaceholder: '0101901234 (no hyphen)',
    cprHelp: 'The CPR number is encrypted and never shown in plaintext.',
    partyKind: 'Renter type',
    partyPerson: 'Private person',
    partySelskab: 'Company',
    cvr: 'CVR number',
    cvrPlaceholder: '12345678',
    cvrLookup: 'Fetch',
    cvrHelp: "We fetch the company name, address, phone and email from the CVR register.",
    cvrInvalid: 'Enter a valid 8-digit CVR number.',
    hasDanishCvr: 'Does the renter have a Danish CVR number?',
    hasDanishCvrHelp: 'Turn off if the company is foreign and uses a foreign registration number. No CVR lookup is performed.',
    foreignReg: 'Foreign registration number',
    foreignRegPlaceholder: 'Enter registration number',
    selskabBadge: 'Company',
    personBadge: 'Person',
    address: 'Address',
    lease: 'Lease',
    status: 'Status',
    statusApplicant: 'Applicant',
    statusActive: 'Active',
    statusFormer: 'Former',
    hasCpr: 'CPR registered',
    noCpr: 'No CPR',
    noResults: 'No renters found.',
    createSuccess: 'Renter created.',
    createError: 'Could not create renter. Please try again.',
    namePlaceholder: 'Jane Doe',
    emailPlaceholder: 'jane@example.com',
    phonePlaceholder: '+45 12 34 56 78',
  },
  leases: {
    title: 'Leases',
    create: 'Create lease',
    address: 'Address',
    unit: 'Unit / door',
    unitPlaceholder: '1st floor, left',
    type: 'Type',
    typeResidential: 'Apartment',
    typeCommercial: 'Commercial',
    typeGarage: 'Garage/carport',
    typeStorage: 'Storage',
    usage: 'Usage',
    usageBolig: 'Residential',
    usageErhverv: 'Commercial',
    usageGarage: 'Garage',
    usageLager: 'Storage',
    usageKontor: 'Office',
    usageKaelder: 'Basement',
    usageAndet: 'Other',
    renter: 'Renter',
    monthlyRent: 'Monthly rent',
    status: 'Status',
    property: 'Property',
    selectProperty: 'Select property',
    noResults: 'No leases found.',
    createSuccess: 'Lease created.',
    createError: 'Could not create lease. Please try again.',
    unassigned: '—',
    area: 'Area (m²)',
    rooms: 'Rooms',
    floor: 'Floor',
    bbrFetching: 'Fetching from BBR …',
    bbrFound: 'Fetched from BBR',
    bbrNotFound: 'No BBR data found for the address — fill in manually.',
    bbrUsageSuggested: 'suggested from BBR',
  },
  contracts: {
    title: 'Contracts',
    create: 'Create contract',
    noResults: 'No contracts found.',
    status: 'Status',
    statusDraft: 'Draft',
    statusSent: 'Sent',
    statusSigned: 'Signed',
    statusActive: 'Active',
    statusTerminated: 'Terminated',
    type: 'Type',
    monthlyRent: 'Monthly rent',
    startDate: 'Move-in date',
    wizardStep1: 'Lease and renter',
    wizardStep2: 'Rental terms',
    wizardStep3: '§ 11 clauses',
    wizardStep4: 'Review and send',
    selectLease: 'Select lease',
    selectRenter: 'Select renter',
    monthlyRentLabel: 'Monthly rent (DKK)',
    depositLabel: 'Deposit (DKK)',
    prepaidRentLabel: 'Prepaid rent (DKK)',
    startDateLabel: 'Move-in date',
    endDateLabel: 'End date (empty = open-ended)',
    addClause: 'Add clause',
    clauseParagraph: 'Paragraph no.',
    clauseContent: 'Clause text',
    removeClause: 'Remove',
    reviewTitle: 'Review contract',
    sendForSigning: 'Send for signing',
    sending: 'Sending...',
    sentConfirmation: 'Contract sent for signing via MitID (Criipto)',
    sentSubtitle: 'All parties will receive an email with the signing link.',
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
  },
} as const

export type EnStrings = typeof en
